import { Character } from "@moe/priv/model/character";
import { debounce } from "@moe/priv/utils";
import { AuthModal } from "@web/components/AuthModal";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback, useState } from "react";

interface Props {
  char: Character;
}

/**
 * A hook providing functionality for liking a card.
 * If the user is not logged in, a modal will be shown prompting the user to sign up.
 *
 * @param card - The card to be liked.
 * @returns The optimistic like count, isLiked state, and a like button handler.
 */
export function useLikeChar({ char }: Props) {
  const { access, createModal, session } = useAppContext();
  const [likeCount, setLikeCount] = useState(char.likeCount);
  const [isLiked, setIsLiked] = useState(char.isLiked);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLikeToggle = useCallback(
    debounce((shouldLike: boolean) => {
      if (shouldLike) {
        access.character.like(char.id);
      } else {
        access.character.unlike(char.id);
      }
    }, 200),
    [char.id, access.character]
  );

  const like = () => {
    // Prevent the card from being clicked when the like button is clicked
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    debouncedLikeToggle(!isLiked);
    setIsLiked(!isLiked);
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
  };

  return {
    likeCount,
    isLiked,
    like
  };
}
